import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import './styles.scss';

// ── Toast ─────────────────────────────────────────────────────────────────────
const Toast = ({ message, visible }) => (
  <div className={`td-toast${visible ? ' show' : ''}`}>
    <svg style={{ width: 18, height: 18, flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
    {message}
  </div>
);

const INITIAL_ENTRIES = [
  {
    id: 'CBP-998234-1',
    display: '112-998234-1',
    ci: 'Inv_882.pdf',
    transport: 'Ocean_882.pdf',
    pct: 100,
    error: false,
    existing: true,   // triggers version conflict modal
    disabled: false,
  },
  {
    id: 'CBP-998234-2',
    display: '112-998234-2',
    ci: 'Inv_883.pdf',
    transport: 'Ocean_883.pdf',
    pct: 100,
    error: false,
    existing: false,
    disabled: false,
  },
  {
    id: 'CBP-998234-3',
    display: '112-998234-3',
    ci: 'Inv_884.pdf',
    transport: 'AWB_884.pdf',
    pct: 0,
    error: true,
    existing: false,
    disabled: true,  // disabled until error resolved
  },
];

const YAMAHA_INITIAL_ENTRIES = [
  {
    id: 'CBP-998234-1',
    display: '112-998234-1',
    ci: 'Inv_JP_882.pdf',
    transport: 'Ocean_JP_882.pdf',
    pct: 100,
    error: false,
    existing: true,
    disabled: false,
  },
  {
    id: 'CBP-998234-2',
    display: '112-998234-2',
    ci: 'Inv_TW_883.pdf',
    transport: 'Ocean_TW_883.pdf',
    pct: 100,
    error: false,
    existing: false,
    disabled: false,
  },
  {
    id: 'CBP-998234-3',
    display: '112-998234-3',
    ci: 'Inv_JP_884.pdf',
    transport: 'AWB_JP_884.pdf',
    pct: 0,
    error: true,
    existing: false,
    disabled: true,
  },
];

const PreAuditVerification = () => {
  const navigate = useNavigate();
  const other = useSelector((state) => state.other);
  const isYamaha = other?.selectedCompany?.Id === '1';

  const [entries, setEntries] = useState(() => isYamaha ? YAMAHA_INITIAL_ENTRIES : INITIAL_ENTRIES);
  const [checked, setChecked] = useState({ 'CBP-998234-1': true, 'CBP-998234-2': true, 'CBP-998234-3': false });
  const [selectedRowForModal, setSelectedRowForModal] = useState(null);

  // Version conflict modal (for existing base record)
  const [versionModalFor, setVersionModalFor] = useState(null); // entry id

  // Split modal (to fix error on row 3)
  const [splitModalOpen, setSplitModalOpen] = useState(false);
  const [fixDate, setFixDate] = useState('');
  const [dateError, setDateError] = useState(false);

  // Toast
  const [toastMsg, setToastMsg] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimer = useRef(null);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const showToast = (msg) => {
    setToastMsg(msg);
    setToastVisible(true);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastVisible(false), 3500);
  };

  // ── Checkbox logic ───────────────────────────────────────────────────────
  const selectableIds = entries.filter((e) => !e.disabled).map((e) => e.id);
  const checkedCount = selectableIds.filter((id) => checked[id]).length;
  const allChecked = checkedCount === selectableIds.length && selectableIds.length > 0;

  const toggleAll = () => {
    const next = !allChecked;
    const c = { ...checked };
    selectableIds.forEach((id) => { c[id] = next; });
    setChecked(c);
  };

  const toggleRow = (id, hasExisting) => {
    if (hasExisting && !checked[id]) {
      // Show version conflict warning first
      setVersionModalFor(id);
      return;
    }
    setChecked((p) => ({ ...p, [id]: !p[id] }));
  };

  // ── Version modal: confirm creates new version & executes ───────────────
  const confirmNewVersion = () => {
    setVersionModalFor(null);
    showToast('Pipeline Executed. Data committed to 19 CFR Audit Radar.');
    setTimeout(() => navigate('/'), 1200);
  };

  // ── Error fix (row 3) ─────────────────────────────────────────────────────
  const handleSaveCorrection = () => {
    if (!fixDate) { setDateError(true); return; }
    setDateError(false);
    setSplitModalOpen(false);
    setEntries((prev) =>
      prev.map((e) =>
        e.id === 'CBP-998234-3'
          ? { ...e, error: false, pct: 100, disabled: false }
          : e
      )
    );
    setChecked((p) => ({ ...p, 'CBP-998234-3': false }));
    showToast('AWB date verified. Entry is now selectable.');
  };

  // ── Execute ───────────────────────────────────────────────────────────────
  const handleExecute = () => {
    const existingSelected = entries.find((e) => checked[e.id] && e.existing);
    if (existingSelected) {
      setVersionModalFor(existingSelected.id);
      return;
    }
    showToast('Pipeline Executed. Data committed to 19 CFR Audit Radar.');
    setTimeout(() => navigate('/'), 1200);
  };

  return (
    <Box className="td-preaudit-wrapper">
      <Toast message={toastMsg} visible={toastVisible} />

      <h3 className="td-section-title">Pre-Audit Extraction Verification (Staging)</h3>
      <p className="td-section-sub">Select verified entries to push to the 19 CFR base tables.</p>

      <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <table className="td-data-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={toggleAll}
                  style={{ cursor: 'pointer' }}
                />
              </th>
              <th>Entry Number</th>
              <th>Commercial Invoice</th>
              <th>Transport (BOL)</th>
              <th>Extraction Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id} className={entry.error ? 'td-row-danger' : ''}>
                <td>
                  <input
                    type="checkbox"
                    disabled={entry.disabled}
                    checked={!!checked[entry.id]}
                    onChange={() => toggleRow(entry.id, entry.existing)}
                    style={{ cursor: entry.disabled ? 'not-allowed' : 'pointer' }}
                  />
                </td>
                <td>
                  <span style={{ fontWeight: 600 }}>{entry.display}</span>
                  {entry.existing && (
                    <span className="td-badge-update" style={{ marginLeft: 8 }}>Existing Base Record</span>
                  )}
                </td>
                <td style={{ fontFamily: 'monospace', color: '#64748b' }}>{entry.ci}</td>
                <td style={{ fontFamily: 'monospace', color: '#64748b' }}>{entry.transport}</td>
                <td>
                  {entry.error
                    ? <strong>Missing Date of Export</strong>
                    : <><span className="td-dot green" />100% Verified</>
                  }
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button
                      className="td-btn-text"
                      onClick={() => { setSelectedRowForModal(entry); setSplitModalOpen(true); }}
                    >
                      View &amp; Edit
                    </button>
                    <button
                      className="td-btn-icon"
                      title="Delete entry"
                      onClick={() => {
                        if (window.confirm(`Delete Entry packet ${entry.display} and associated files?`)) {
                          setEntries((prev) => prev.filter((e) => e.id !== entry.id));
                          setChecked((p) => { const c = { ...p }; delete c[entry.id]; return c; });
                          showToast(`Entry packet ${entry.display} and associated files deleted.`);
                        }
                      }}
                    >
                      <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="td-footer-action">
          <span style={{ color: '#64748b', fontSize: 13 }}>
            {checkedCount} of {selectableIds.length} entries selected
          </span>
          <button
            className="td-primary-btn"
            disabled={checkedCount === 0}
            style={checkedCount === 0 ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            onClick={handleExecute}
          >
            Execute Selected Entries ({checkedCount}) →
          </button>
        </div>
      </div>

      {/* ── Version Conflict Modal ──────────────────────────────────────── */}
      <div className={`td-modal-overlay${versionModalFor ? ' open' : ''}`}>
        <div className="td-modal-box">
          <div className="td-modal-title">
            <svg width="20" height="20" fill="none" stroke="#f59e0b" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Version Conflict Detected
          </div>
          <p className="td-modal-text">
            A base record for entry <strong>{versionModalFor}</strong> already exists in the 19 CFR
            audit tables. Executing this packet will create a new versioned record while preserving
            the original.
          </p>
          <p className="td-modal-text">
            Any outstanding PSC resolutions tied to the previous version will be flagged for re-review.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
            <button className="td-btn-outline" onClick={() => setVersionModalFor(null)}>Cancel</button>
            <button className="td-primary-btn" onClick={confirmNewVersion}>
              Create New Version &amp; Execute
            </button>
          </div>
        </div>
      </div>

      {/* ── Split Modal: Fix AWB date error ────────────────────────────── */}
      <div className={`td-modal-overlay${splitModalOpen ? ' open' : ''}`}>
        <div className="td-modal-split">

          {/* Left: PDF mockup */}
          <div className="td-pane-left">
            <div className="td-pdf-header">
              <span>Source Document: <strong>AWB_884.pdf</strong></span>
            </div>
            <div className="td-pdf-mockup">
              <div className="td-pdf-page">
                <div className="td-pdf-page-header">
                  <div>
                    <h1 style={{ fontSize: 22, fontWeight: 'bold', letterSpacing: 1, margin: 0 }}>AIR WAYBILL</h1>
                    <div style={{ fontSize: 12 }}>Not Negotiable</div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 14 }}><strong>AWB: 409-2234</strong></div>
                </div>
                <div className="td-pdf-grid">
                  <div className="td-pdf-box">
                    <strong>Shipper</strong><br />
                    DASI AEROSPACE<br />TOKYO, JAPAN
                  </div>
                  <div className="td-pdf-box">
                    <strong>Consignee</strong><br />
                    DASI USA HQ<br />LOS ANGELES, CA
                  </div>
                </div>
                <div className="td-pdf-grid">
                  <div className="td-pdf-box">
                    <strong>Airport of Departure</strong><br />
                    SHANGHAI (PVG)
                  </div>
                  <div className="td-pdf-box td-pdf-error-box">
                    <strong style={{ color: '#ef4444' }}>Date of Export</strong><br />
                    <span className="td-blur-text">2026-02-20 / ILLEGIBLE</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: fix form */}
          <div className="td-pane-right">
            {/* Doc selector — shown for all pre-audit entries */}
            {selectedRowForModal && (
              <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #e2e8f0' }}>
                <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>
                  Entry {selectedRowForModal.display} Documents:
                </label>
                <select className="td-inline-select" style={{ width: '100%' }}>
                  <option>Transport: {selectedRowForModal.transport}{selectedRowForModal.error ? ' (Error)' : ' (Verified)'}</option>
                  <option>Invoice: {selectedRowForModal.ci} (Verified)</option>
                  <option>Master Data: DASI_{selectedRowForModal.display.split('-')[1]}.xlsx (Verified)</option>
                </select>
              </div>
            )}
            {selectedRowForModal?.error ? (<>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 24 }}>
              Enter the correct Date of Export from the physical document to re-validate this entry.
            </p>

            <div className="td-form-group">
              <label>Manifest / AWB Number</label>
              <input type="text" value="409-2234" readOnly style={{ background: '#f1f5f9', color: '#64748b' }} />
            </div>
            <div className="td-form-group">
              <label>
                Date of Export <span style={{ color: '#ef4444' }}>*Required</span>
              </label>
              <input
                type="date"
                className={dateError ? 'td-input-error' : ''}
                value={fixDate}
                onChange={(e) => { setFixDate(e.target.value); setDateError(false); }}
              />
              {dateError && (
                <span className="td-error-msg">This field is required to unlock the entry.</span>
              )}
            </div>

            <div style={{ marginTop: 'auto', paddingTop: 24 }}>
              <button
                className="td-primary-btn"
                style={{ width: '100%' }}
                onClick={handleSaveCorrection}
              >
                Save &amp; Re-Validate Entry
              </button>
              <button
                className="td-btn-text"
                style={{ width: '100%', marginTop: 12, color: '#64748b', textAlign: 'center', display: 'block' }}
                onClick={() => { setSplitModalOpen(false); setDateError(false); }}
              >
                Cancel
              </button>
            </div>
          </>) : (
            <div>
              <h2 style={{ marginTop: 0, fontSize: 18 }}>Document Verified</h2>
              <p style={{ fontSize: 13, color: '#64748b' }}>All documents for this entry have been extracted and verified successfully.</p>
              <div style={{ marginTop: 16, padding: 16, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6 }}>
                <strong style={{ color: '#166534', fontSize: 14 }}>✓ 100% Verified — No Issues Found</strong>
              </div>
              <button
                className="td-btn-text"
                style={{ marginTop: 24, color: '#64748b' }}
                onClick={() => { setSplitModalOpen(false); }}
              >
                Close
              </button>
            </div>
          )}
          </div>
        </div>
      </div>
    </Box>
  );
};

export default PreAuditVerification;
